import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Participant,
  ParticipantFieldType,
  Recruitment,
  useMe,
  useParticipant,
  useParticipantFieldsByCategory,
  useUpdateParticipant,
} from '@app/api';
import * as Yup from 'yup';
import moment from 'moment';

import {
  SubmitButton,
  InformationBox,
  useToast,
  CenteredLoader,
} from '@app/core';
import { Theme } from '@app/theme';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { SubmitButtonsLine } from '@app/form';
import { ParticipantInputByName } from '../../Profile/components/ParticipantInputByName';
import { ProfileCategorySection } from '../../Profile/components/ProfileCategorySection';
import { ProfileCategory } from './useProfileSteps';
import { FakerProfileButton } from '../faker';

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    [theme.media.mobile]: {
      width: '100%',
    },
  },
  buttonLine: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.marginBase * 6,
    gap: theme.marginBase,
  },
  title: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 3,
  },
}));

interface Props {
  criteriaRefs: string[];
  onSubmit: (participant: Partial<Participant>) => any;
  participantId?: string;
  currentFieldsByCategories: ProfileCategory[];
  recruitment?: Recruitment;
}
export const StudyProfile = ({
  criteriaRefs,
  onSubmit,
  participantId,
  currentFieldsByCategories,
  recruitment,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const fields = useParticipantFieldsByCategory();

  const { data: me } = useMe(true);

  const { data: currentParticipant, isLoading: isParticipantLoading } =
    useParticipant(participantId);
  const { mutateAsync: updateParticipant } =
    useUpdateParticipant(participantId);

  const connected = !!currentParticipant?.panelId;

  const updateProfile = useCallback(
    async (profile: Partial<Participant>) => {
      const updatedData = {} as Partial<Participant>;
      for (const ref of criteriaRefs) {
        // @ts-ignore
        updatedData[ref] = profile[ref];
      }

      await updateParticipant(updatedData);
      toast.saved();
    },
    [toast, updateParticipant],
  );

  const onFormikSubmit = useCallback(
    async (participant: Partial<Participant>, helpers: FormikHelpers<any>) => {
      try {
        helpers.setSubmitting(true);
        if (connected) {
          await updateProfile(participant);
        }
        await onSubmit(participant);
        helpers.setSubmitting(false);
      } catch (e) {
        toast.exception(e);
        helpers.setSubmitting(false);
      }
    },
    [onSubmit, connected, toast, updateProfile],
  );

  const displayedFields = useMemo(
    () => currentFieldsByCategories.flatMap(({ fields }) => fields).flat(),
    [currentFieldsByCategories],
  );

  const initialValues: Partial<Participant> = useMemo(() => {
    const res = {} as Partial<Participant>;
    for (const field of displayedFields) {
      res[field.name] = undefined;
      if (currentParticipant) {
        // @ts-ignore
        res[field.name] = currentParticipant[field.name];
      }
    }
    return res;
  }, [currentParticipant, displayedFields]);

  const validationSchema = useMemo(() => {
    const shape: Yup.ObjectShape = {};

    for (const field of displayedFields) {
      switch (field.type) {
        case ParticipantFieldType.DATE:
          if (field.name === 'birthdate') {
            shape.birthdate = Yup.date()
              .typeError('form.errors.birthdate.invalid')
              .max(
                moment().subtract(18, 'years').toDate(),
                'form.errors.birthdate.max',
              )
              .min(
                moment().subtract(100, 'years').toDate(),
                'form.errors.birthdate.min',
              )
              .required();
          } else {
            shape[field.name] = Yup.date().required();
          }
          break;
        case ParticipantFieldType.MULTIPLE_CHOICE:
        case ParticipantFieldType.TAGS:
        case ParticipantFieldType.LOCALES:
          shape[field.name] = Yup.array(Yup.string()).min(1).required();
          break;

        case ParticipantFieldType.BOOLEAN:
          shape[field.name] = Yup.boolean().required();
          break;
        case ParticipantFieldType.NUMBER:
          shape[field.name] = Yup.number().required();
          break;
        case ParticipantFieldType.GEOLOCATION:
        default:
          shape[field.name] = Yup.string().required();
          break;
      }
    }
    return Yup.object().shape(shape);
  }, [displayedFields]);

  useEffect(() => {
    if (!displayedFields.length && Object.values(fields).length) {
      onSubmit({});
    }
  }, [fields, displayedFields]);

  if (
    (me && participantId && isParticipantLoading) ||
    !displayedFields.length
  ) {
    return <CenteredLoader />;
  }

  return (
    <Formik
      onSubmit={onFormikSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <Form>
        {connected && (
          <>
            <h1 className={classes.title}>
              {t('application.profile.connected.title')}
            </h1>

            <InformationBox noIcon>
              <div>{t('application.profile.connected.explanation')}</div>
              <SubmitButtonsLine>
                <SubmitButton
                  small
                  className={classes.button}
                  variant="transparent"
                  type="button"
                  href=""
                >
                  {t('application.profile.connected.terms')}
                </SubmitButton>
              </SubmitButtonsLine>
            </InformationBox>
          </>
        )}

        {currentFieldsByCategories.map(({ category, fieldNames }) => (
          <ProfileCategorySection category={category} noDesc>
            {fieldNames.map((name) => (
              <ParticipantInputByName fieldName={name} />
            ))}
          </ProfileCategorySection>
        ))}

        <div className={classes.buttonLine}>
          <FakerProfileButton recruitment={recruitment} />
          <SubmitButton small className={classes.button} withNextArrow>
            {connected
              ? t('application.profile.connected.continue')
              : t('application.profile.annon.continue')}
          </SubmitButton>
        </div>
      </Form>
    </Formik>
  );
};
